import { cn } from "@/lib/utils";
import type { Product } from "@/features/catalog/types";

type ProductImageProps = {
    product: Pick<Product, "tone" | "glyph" | "name"> &
        Partial<Pick<Product, "images" | "show_picture">>;
    size?: "card" | "hero" | "thumb";
    className?: string;
    /** Optional variation used by the PDP gallery to render different tints. */
    variant?: 0 | 1 | 2 | 3;
};

/**
 * ProductImage — gradient + glyph placeholder used everywhere by default.
 *
 * When the admin has toggled "Show Product Picture" on a product AND at
 * least one image has been uploaded, we render the uploaded picture
 * instead of the glyph placeholder. The `variant` prop indexes into
 * `product.images` so the PDP gallery thumbnails cycle through real
 * uploads when available; it falls back to the first image otherwise.
 */
export function ProductImage({
    product,
    size = "card",
    className,
    variant = 0,
}: ProductImageProps) {
    const images = product.images ?? [];
    const showPicture = Boolean(product.show_picture) && images.length > 0;

    if (showPicture) {
        const src = images[variant] ?? images[0];
        return (
            <div
                data-testid="product-image"
                data-mode="picture"
                className={cn("relative overflow-hidden bg-muted", className)}
            >
                <img
                    src={src}
                    alt={product.name}
                    loading="lazy"
                    className="h-full w-full object-cover"
                />
            </div>
        );
    }

    const [a, b] = product.tone;
    const angles = [135, 225, 45, 315];
    const angle = angles[variant] ?? 135;
    const stops = variant % 2 === 0 ? [a, b] : [b, a];

    const sizeClass =
        size === "hero"
            ? "text-9xl"
            : size === "thumb"
              ? "text-3xl"
              : "text-6xl";

    return (
        <div
            data-testid="product-image"
            data-mode="glyph"
            className={cn("relative overflow-hidden", className)}
            style={{
                background: `linear-gradient(${angle}deg, ${stops[0]}, ${stops[1]})`,
            }}
        >
            {/* Center glyph */}
            <span
                className={cn(
                    "absolute inset-0 grid place-items-center font-black tracking-tight text-white/70 mix-blend-overlay",
                    sizeClass,
                )}
            >
                {product.glyph || product.name[0]}
            </span>
            {/* Sheen */}
            <span
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0"
            />
        </div>
    );
}

export default ProductImage;
